import React from 'react'
import { Search, MapPin, Building2, Landmark, Pencil, Trash2 } from 'lucide-react'
import type { Alert } from '../services/api'

interface Props {
  alert: Alert
  isConfirming: boolean
  onRequestDelete: (id: string) => void
  onDelete: (id: string) => void
  onEdit: (alert: Alert) => void
  onClick: (alert: Alert) => void
}

interface Pill {
  icon: React.ReactNode
  text: string
  cls: string
}

export function filterPills(filters: Record<string, unknown>): Pill[] {
  const pills: Pill[] = []
  const keywords: string[] = Array.isArray(filters.keywords)
    ? (filters.keywords as string[])
    : filters.keyword ? [filters.keyword as string] : []
  keywords.forEach(kw => pills.push({ icon: <Search size={10} />, text: kw, cls: 'bg-gray-100 text-gray-700' }))
  if (filters.region)   pills.push({ icon: <MapPin size={10} />,    text: filters.region as string,   cls: 'bg-blue-100 text-blue-700' })
  if (filters.sector)   pills.push({ icon: <Building2 size={10} />, text: filters.sector as string,   cls: 'bg-green-100 text-green-700' })
  const entiArr: string[] = Array.isArray(filters.enti) ? (filters.enti as string[]) : []
  entiArr.forEach(e => pills.push({ icon: <Landmark size={10} />, text: e, cls: 'bg-sky-100 text-sky-700' }))
  return pills
}

export function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric' })
}

export default function AlertCard({ alert, isConfirming, onRequestDelete, onDelete, onEdit, onClick }: Props) {
  const pills = filterPills(alert.filters)

  return (
    <div
      onClick={() => onClick(alert)}
      className="bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow cursor-pointer p-5 flex flex-col gap-3"
    >
      {/* Top row */}
      <div className="flex items-start justify-between gap-3">
        <span className="font-semibold text-gray-900 text-base leading-snug">{alert.name}</span>
        <span className="shrink-0 text-xs font-medium bg-sky-100 text-sky-700 rounded-full px-2.5 py-1">
          {alert.match_count} match
        </span>
      </div>

      {/* Filter pills */}
      <div className="flex flex-wrap gap-1.5">
        {pills.length === 0
          ? <span className="text-xs bg-gray-100 text-gray-500 rounded-full px-2.5 py-1">Tutti i concorsi</span>
          : pills.map((p, i) => (
            <span key={i} className={`inline-flex items-center gap-1 text-xs font-medium rounded-full px-2.5 py-1 ${p.cls}`}>
              {p.icon}{p.text}
            </span>
          ))
        }
      </div>

      {/* Bottom row */}
      <div className="flex items-center justify-between mt-1">
        <span className="text-xs text-gray-400">Creato il {formatDate(alert.created_at)}</span>
        <div className="flex items-center gap-2">
          <button
            onClick={e => { e.stopPropagation(); onEdit(alert) }}
            className="p-1 sm:p-1.5 text-gray-400 hover:text-blue-500 transition-colors"
            aria-label="Modifica avviso"
          >
            <Pencil size={16} className="sm:hidden" />
            <Pencil size={18} className="hidden sm:block" />
          </button>
          <button
            onClick={e => { e.stopPropagation(); onRequestDelete(alert.id) }}
            className="p-1 sm:p-1.5 text-gray-400 hover:text-red-500 transition-colors"
            aria-label="Elimina avviso"
          >
            <Trash2 size={16} className="sm:hidden" />
            <Trash2 size={18} className="hidden sm:block" />
          </button>
        </div>
      </div>

      {/* Confirm delete */}
      {isConfirming && (
        <div
          onClick={e => e.stopPropagation()}
          className="border-t border-red-100 pt-3 flex flex-col gap-2"
        >
          <p className="text-xs text-red-600 font-medium">
            Eliminare <span className="font-bold">"{alert.name}"</span>? Perderai tutti gli aggiornamenti su questo avviso.
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => onDelete(alert.id)}
              className="text-xs px-3 py-1.5 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors"
            >
              Elimina
            </button>
            <button
              onClick={e => { e.stopPropagation(); onRequestDelete('') }}
              className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
            >
              Annulla
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
